import React, { useEffect, useRef, useState } from 'react';

/**
 * 🛋️ CouchColumn (09-23, MOVIE_NIGHT_UX_HANDOFF §3.4 v2, Nina's rulings 9-10):
 * the couch as a page-level COLUMN humans can post to, instead of a lower
 * third that evaporates in 10 seconds. Mounted in the rail slot the cinema
 * gate vacates (desktop), under the art on the sheet, under the film on
 * phones (`compact`). Survives a reload because the page hydrates it from the
 * stored quip rows; after The End it stays as scrollback ("The couch,
 * tonight") and the input goes.
 *
 * Pure-ish: `items` arrive ready, oldest first, { id, sender_name, avatar_url,
 * text, at }; newest renders at the bottom and the list follows new items
 * ONLY while the reader was already at the bottom (scroll up to read back and
 * nothing yanks you). `onPost(text)` is the page's `@watchparty` lane; it may
 * return a promise: the box clears on success, keeps the words on failure.
 * A sending ref guards the double Enter (the 09-20 composer lesson: state is
 * not a lock). ⚠️ No global box-sizing here · inputs ≥ 16px on phones.
 */
export const COUCH_MAX = 240;
export const COUCH_EMPTY = 'The couch is quiet. The projector warms up.';
export const COUCH_EMPTY_ENDED = 'The couch stayed quiet tonight.';
export const COUCH_ENDED_TITLE = 'The couch, tonight';
export const COUCH_PLACEHOLDER = 'Say something to the couch';
const STICK_PX = 32; // "at the bottom" = within this many px of it

const when = (at) => {
  const ms = typeof at === 'number' ? at : Date.parse(at);
  if (!Number.isFinite(ms)) return undefined;
  try { return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch (e) { return undefined; }
};

export default function CouchColumn({ items, title = 'The couch', onPost = null, compact = false, ended = false, THEME = null, maxHeight = null }) {
  const list = Array.isArray(items) ? items : [];
  const T = THEME || {};
  const accent = T.accent || '#ce93d8';
  const muted = T.textMuted || '#9a8fa8';
  const ink = T.text || '#eee';
  const count = list.length;

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const stickRef = useRef(true);   // was the reader at the bottom before the last item landed?
  const sendingRef = useRef(false); // the lock; `sending` state only draws the button

  // follow new items only when already at the bottom (declared below every value it closes over)
  useEffect(() => {
    const el = listRef.current;
    if (!el || !stickRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [count]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    stickRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) <= STICK_PX;
  };

  const post = async () => {
    const t = String(text || '').trim().slice(0, COUCH_MAX);
    if (!t || typeof onPost !== 'function' || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      await onPost(t);
      setText('');
    } catch (e) {
      setError((e && e.message) || 'That did not send. Try again.');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const showInput = typeof onPost === 'function' && !ended;
  const heading = ended ? COUCH_ENDED_TITLE : title;
  const rowSize = compact ? 12 : 13;

  return (
    <div data-couch-column={ended ? 'ended' : 'live'} style={{
      display: 'flex', flexDirection: 'column', gap: compact ? 4 : 6, minWidth: 0, minHeight: 0,
      boxSizing: 'border-box', maxWidth: '100%', ...(maxHeight ? { maxHeight } : {}),
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
        <b style={{ fontSize: compact ? 10.5 : 11.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: accent, whiteSpace: 'nowrap' }}>
          🛋️ {heading}
        </b>
        {count > 0 && (
          <span style={{ fontSize: 11, color: muted, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
        )}
      </div>
      <div ref={listRef} onScroll={onScroll} data-couch-list="1" style={{
        flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
        display: 'flex', flexDirection: 'column', gap: compact ? 4 : 6, paddingRight: 2,
      }}>
        {count === 0 && (
          <div style={{ fontSize: compact ? 12 : 12.5, fontStyle: 'italic', color: muted, lineHeight: 1.4 }}>
            {ended ? COUCH_EMPTY_ENDED : COUCH_EMPTY}
          </div>
        )}
        {list.map((it, i) => (
          <div key={it.id || `${it.at || 'x'}-${i}`} data-couch-row="1" title={when(it.at)} style={{
            display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: rowSize, lineHeight: 1.35, color: ink, minWidth: 0,
          }}>
            {it.avatar_url ? (
              <img src={it.avatar_url} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginTop: 1 }} />
            ) : (
              <span aria-hidden="true" style={{ flexShrink: 0 }}>🛋️</span>
            )}
            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              <span style={{ fontWeight: 800, color: '#ce93d8', marginRight: 5 }}>{it.sender_name}</span>
              {it.text}
            </span>
          </div>
        ))}
      </div>
      {showInput && (
        <form onSubmit={(e) => { e.preventDefault(); post(); }} style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
          <input
            data-couch-input="1"
            type="text"
            value={text}
            maxLength={COUCH_MAX}
            placeholder={COUCH_PLACEHOLDER}
            aria-label={COUCH_PLACEHOLDER}
            autoComplete="off"
            enterKeyHint="send"
            onChange={(e) => setText(String(e.target.value || '').slice(0, COUCH_MAX))}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.shiftKey || (e.nativeEvent && e.nativeEvent.isComposing)) return;
              e.preventDefault();
              post();
            }}
            style={{
              font: 'inherit', fontSize: 16, flex: '1 1 auto', minWidth: 0, width: '100%', boxSizing: 'border-box',
              padding: '7px 10px', borderRadius: 8, border: `1px solid ${accent}55`,
              background: 'rgba(255,255,255,0.05)', color: ink,
            }}
          />
          <button type="submit" data-couch-send="1" disabled={sending || !text.trim()} style={{
            font: 'inherit', fontSize: 12.5, fontWeight: 800, padding: '7px 10px', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
            background: 'transparent', border: `1px solid ${accent}`, color: accent, opacity: (sending || !text.trim()) ? 0.55 : 1,
          }}>
            Send
          </button>
        </form>
      )}
      {error && (
        <div role="alert" style={{ fontSize: 12, color: T.error || '#f48fb1' }}>{error}</div>
      )}
    </div>
  );
}
